import { CheckIcon, GearIcon } from "@radix-ui/react-icons";
import { Box, Button, Dialog, Flex, IconButton } from "@radix-ui/themes";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useHotkeys } from "react-hotkeys-hook";
import { useSearchParams } from "react-router-dom";
import { useMedia } from "react-use";
import {
  PlayerState,
  setSegments,
  updateCurrentTime,
  updateLiveEdge,
} from "@/entities/player";
import { useAppDispatch, useAppSelector } from "@/app/appStore";
import { calculateArbitraryDateRange } from "@/shared/helpers/calculate-arbitrary-date-range";
import { useMediaPlayer } from "@/shared/ui";
import { CurrentTime } from "@/shared/ui/current-time";
import { InputFloatingLabel } from "@/shared/ui/input-floating-label";
import { LiveButton } from "./live-button";
import { LiveDelay } from "./live-delay";
import { PlayPauseButton } from "./play-pause-button";
import { ZoomSlider } from "./zoom-slider";

type ControlsProps = {
  videoParams: PlayerState["videoStream"];
  setVideoParams: (videoParams: Partial<PlayerState["videoStream"]>) => void;
};

export function Controls({ videoParams, setVideoParams }: ControlsProps) {
  const mediaPlayer = useMediaPlayer();
  const [from, setFrom] = useState<string>(videoParams.fromTime);
  const [to, setTo] = useState<string>(videoParams.toTime);
  const [isApplied, setIsApplied] = useState(false);
  const preferences = useAppSelector((state) => state.player.preferences);
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialized = useRef(false);

  useEffect(() => {
    if (isInitialized.current) return;

    const timestamp = searchParams.get("timestamp");
    const from = searchParams.get("from");
    const to = searchParams.get("to");
    const cameraId = searchParams.get("camera");
    const isLive = searchParams.get("is_live") === "true";

    const videoParams: Partial<PlayerState["videoStream"]> = {};

    if (timestamp) {
      const timestampDate = new Date(timestamp).getTime();
      const [arbitraryFromTime, arbitraryToTime] = calculateArbitraryDateRange(
        timestampDate,
        preferences.padding
      );
      videoParams.arbitraryFromTime = arbitraryFromTime;
      videoParams.arbitraryToTime = arbitraryToTime;

      dispatch(updateCurrentTime(timestampDate));
    } else {
      if (from) {
        videoParams.fromTime = from;
      }
      if (to) {
        videoParams.toTime = to;
      }
    }

    if (cameraId) {
      videoParams.cameraId = cameraId;
    }
    if (isLive) {
      videoParams.isLive = isLive;
    }

    setVideoParams(videoParams);
    isInitialized.current = true;
  }, [searchParams, isInitialized]);

  const hasChanged = () => {
    if (videoParams.isLive && from && to) {
      return true;
    }

    return from !== videoParams.fromTime || to !== videoParams.toTime;
  };

  const hasValidFromTo = () => {
    return (
      !!from &&
      !!to &&
      !Number.isNaN(new Date(from as string).getTime()) &&
      !Number.isNaN(new Date(to).getTime()) &&
      new Date(from) < new Date(to)
    );
  };

  const onApplyChanges = () => {
    if (!hasChanged()) return;

    if (!videoParams.cameraId) {
      toast.error("Please select a camera");
      return;
    }

    if (!hasValidFromTo()) {
      toast.error("Invalid from and to dates");
      return;
    }

    setVideoParams({
      fromTime: from,
      toTime: to,
      isLive: false,
    });
    setSearchParams((prev) => {
      prev.set("from", from);
      prev.set("to", to);
      prev.delete("is_live");

      return prev;
    });

    setIsApplied(true);

    setTimeout(() => {
      setIsApplied(false);
    }, 1000);
  };

  const onLive = () => {
    if (!videoParams.cameraId) {
      toast.error("Please select a camera");
      return;
    }

    if (!videoParams.isLive) {
      setVideoParams({
        ...videoParams,
        isLive: true,
      });
      setSearchParams((prev) => {
        prev.set("is_live", "true");
        return prev;
      });
    } else {
      mediaPlayer.seekToOriginalLive();
      mediaPlayer.play();
    }
  };

  const onReset = () => {
    setVideoParams({
      ...videoParams,
      isLive: false,
      fromTime: "",
      toTime: "",
      arbitraryFromTime: null,
      arbitraryToTime: null,
    });
    setSearchParams({
      camera: searchParams.get("camera") || "",
    });
    dispatch(setSegments([]));
    dispatch(updateLiveEdge(null));
    setFrom("");
    setTo("");
    setIsApplied(false);
    dispatch(updateCurrentTime(null));
  };

  useHotkeys(
    "space",
    () => {
      if (!mediaPlayer.isReady()) return;

      if (mediaPlayer.isPaused()) {
        mediaPlayer.play();
      } else {
        mediaPlayer.pause();
      }
    },
    {
      preventDefault: true,
    }
  );

  useHotkeys("right", () => {
    if (!mediaPlayer.isReady()) return;

    const seekThreshold = preferences.seekThreshold / 1000;
    const newTime = mediaPlayer.time() + seekThreshold;

    if (newTime > mediaPlayer.duration()) {
      toast.error("Cannot seek beyond the end of the video");
      return;
    }

    mediaPlayer.seek(newTime);
  });

  useHotkeys("left", () => {
    if (!mediaPlayer.isReady()) return;

    const seekThreshold = preferences.seekThreshold / 1000;
    const newTime = mediaPlayer.time() - seekThreshold;

    if (newTime < 0) {
      toast.error("Cannot seek before the start of the video");
      return;
    }

    mediaPlayer.seek(newTime);
  });

  const isDesktop = useMedia("(min-width: 720px)");

  if (!isDesktop) {
    return (
      <Box>
        <Flex gap="2" p="2" align="center" justify="between" className="w-full">
          <CurrentTime />
          <LiveDelay />
        </Flex>
        <Flex gap="2" p="2" align="center" justify="between" className="w-full">
          <Flex gap="2" align="center">
            <Dialog.Root>
              <Dialog.Trigger>
                <IconButton color="gray" variant="soft">
                  <GearIcon />
                </IconButton>
              </Dialog.Trigger>

              <Dialog.Content maxWidth="350px">
                <Dialog.Title>Filters</Dialog.Title>

                <Flex direction="column" gap="3">
                  <InputFloatingLabel
                    value={from}
                    onChange={(e) => {
                      setFrom(e.target.value);
                    }}
                    id="from"
                    label="From"
                    className="w-full"
                    height={40}
                    placeholder="2025-01-01T00:00:00.000Z"
                  />
                  <InputFloatingLabel
                    value={to}
                    onChange={(e) => {
                      setTo(e.target.value);
                    }}
                    id="to"
                    label="To"
                    height={40}
                    className="w-full"
                    placeholder="2025-01-01T00:00:00.000Z"
                  />
                  <Button
                    variant="soft"
                    onClick={onApplyChanges}
                    disabled={!hasChanged() || isApplied}
                  >
                    {isApplied ? (
                      <>
                        <CheckIcon /> Applied
                      </>
                    ) : (
                      "Apply"
                    )}
                  </Button>
                  <Button variant="soft" color="gray" onClick={onReset}>
                    Reset
                  </Button>
                  <Dialog.Close>
                    <Button variant="soft" color="gray">
                      Close
                    </Button>
                  </Dialog.Close>
                </Flex>
              </Dialog.Content>
            </Dialog.Root>
          </Flex>
          <Flex gap="5" align="center" justify="end" className="grow" pr="2">
            <div className="absolute left-1/2 -translate-x-[8px]">
              <PlayPauseButton />
            </div>
            <LiveButton onClick={onLive} />
          </Flex>
        </Flex>
      </Box>
    );
  }

  return (
    <Box>
      <Flex gap="2" p="2" align="center" justify="between" className="w-full">
        <Flex gap="2" align="center">
          <InputFloatingLabel
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
            }}
            id="from"
            label="From"
            className="w-[220px]"
            placeholder="2025-01-01T00:00:00.000Z"
          />
          <InputFloatingLabel
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
            }}
            id="to"
            label="To"
            className="w-[220px]"
            placeholder="2025-01-01T00:00:00.000Z"
          />
          <Button
            variant="soft"
            onClick={onApplyChanges}
            disabled={!hasChanged() || isApplied}
          >
            {isApplied ? (
              <>
                <CheckIcon /> Applied
              </>
            ) : (
              "Apply"
            )}
          </Button>
          <Button variant="soft" color="gray" onClick={onReset}>
            Reset
          </Button>
        </Flex>
        <Flex gap="5" align="center" justify="end" className="grow" pr="4">
          <PlayPauseButton />
          <CurrentTime />
          <LiveDelay />
          <LiveButton onClick={onLive} />
          <ZoomSlider />
        </Flex>
      </Flex>
    </Box>
  );
}
